import { Component, OnInit, ViewChild } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FileUploadModule } from 'primeng/fileupload';
import { DrawerModule } from 'primeng/drawer';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { AccordionModule } from 'primeng/accordion';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { TooltipModule } from 'primeng/tooltip';
import { Drawer, DrawerField } from '../../components/drawer/drawer';
import { OnlineHandbookService } from '../../core/services/online-handbook.service';
import { IOnlineHandbook } from '../../core/models/online-handbook.model';
import { FileService } from '../../core/services/file';
import { FaqService } from '../../core/services/faq.service';
import { VideoGuideService } from '../../core/services/video-guide.service';
import { IFaq } from '../../core/models/faq.model';
import { IVideoGuide } from '../../core/models/video-guide.model';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HasPermissionDirective } from '../../core/directives/has-permission.directive';

@Component({
  selector: 'app-online-handbook',
  imports: [
    Menu, 
    CommonModule, 
    ButtonModule, 
    FileUploadModule, 
    DrawerModule, 
    InputTextModule, 
    FormsModule, 
    DividerModule, 
    CardModule, 
    AccordionModule, 
    Drawer,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    HasPermissionDirective
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './online-handbook.html',
  styleUrl: './online-handbook.scss'
})
export class OnlineHandbook implements OnInit {
  @ViewChild('onlineHandbookDrawer') onlineHandbookDrawer!: Drawer;
  @ViewChild('faqDrawerComponent') faqDrawerComponent!: Drawer;
  @ViewChild('videoGuideDrawerComponent') videoGuideDrawerComponent!: Drawer;
  
  items: MenuItem[] = [];
  onlineHandBookDrawer: boolean = false;
  faqDrawer: boolean = false;
  videoGuideDrawer: boolean = false;
  value: string = '';

  // Section visibility
  showOnlineHandbook: boolean = true;
  showFaqs: boolean = false;
  showVideoGuides: boolean = false;
  activeSection: 'onlineHandbook' | 'faqs' | 'videoGuides' = 'onlineHandbook';

  // Online handbook data
  onlineHandbooks: IOnlineHandbook[] = [];
  loading: boolean = false;
  editingHandbook: IOnlineHandbook | null = null;
  
  // Load more functionality
  displayedHandbooks: IOnlineHandbook[] = [];
  itemsPerPage: number = 4;
  currentPage: number = 1;
  hasMoreHandbooks: boolean = false;

  // FAQ data
  faqs: IFaq[] = [];
  faqLoading: boolean = false;
  editingFaq: IFaq | null = null;
  
  // Load more functionality for FAQs
  displayedFaqs: IFaq[] = [];
  faqsPerPage: number = 5;
  currentFaqPage: number = 1;
  hasMoreFaqs: boolean = false;

  // Video guide data
  videoGuides: IVideoGuide[] = [];
  videoGuideLoading: boolean = false;
  editingVideoGuide: IVideoGuide | null = null;
  
  // Load more functionality for video guides
  displayedVideoGuides: IVideoGuide[] = [];
  videoGuidesPerPage: number = 6;
  currentVideoGuidePage: number = 1;
  hasMoreVideoGuides: boolean = false;

  // Window reference for opening links
  window = window;

  // Drawer configurations
  onlineHandbookFields: DrawerField[] = [
    { name: 'title', label: 'Title', type: 'text', required: true, placeholder: 'Title' },
    { name: 'description', label: 'Description', type: 'text', required: true, placeholder: 'Description', maxLength: 100 },
    { name: 'sourceLink', label: 'Source Link', type: 'text', required: true, placeholder: 'notion.com' },
    { name: 'icon', label: 'Icon', type: 'file', required: true, accept: 'image/*', maxFileSize: 1000000 }
  ];

  faqFields: DrawerField[] = [
    { name: 'question', label: 'Question', type: 'text', required: true, placeholder: 'Enter your question' },
    { 
      name: 'answer', 
      label: 'Answer', 
      type: 'editor', 
      required: true, 
      placeholder: 'Enter your detailed answer with rich formatting...',
      editorHeight: '600px',
      editorStyle: {
        'height': '300px',
        'font-size': '14px',
        'border': '1px solid #d1d5db',
        'border-radius': '6px'
      }
    }
  ];

  videoGuideFields: DrawerField[] = [
    { name: 'title', label: 'Title', type: 'text', required: true, placeholder: 'Title' },
    { name: 'sourceLink', label: 'Source Link', type: 'text', required: true, placeholder: 'Video URL' },
    { name: 'thumbnail', label: 'Thumbnail', type: 'file', required: true, accept: 'image/*', maxFileSize: 1000000 }
  ];

  constructor(
    private onlineHandbookService: OnlineHandbookService,
    private faqService: FaqService,
    private videoGuideService: VideoGuideService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private fileService: FileService,
    private sanitizer: DomSanitizer
  ) {}

  // Method to safely render HTML content with proper link and list handling
  sanitizeHtml(content: string): SafeHtml {
    if (!content) return '';
    
    // Process the content to ensure links and lists work correctly
    let processedContent = content;
    
    // Ensure links have proper target="_blank" and rel="noopener noreferrer"
    processedContent = processedContent.replace(
      /<a\s+href=/gi,
      '<a target="_blank" rel="noopener noreferrer" href='
    );
    
    // Add CSS classes to links for styling
    processedContent = processedContent.replace(
      /<a\s+/gi,
      '<a class="content-link" '
    );
    
    // Add CSS classes to lists for proper styling
    processedContent = processedContent.replace(
      /<ul\s+/gi,
      '<ul class="content-list content-ul" '
    );
    
    processedContent = processedContent.replace(
      /<ol\s+/gi,
      '<ol class="content-list content-ol" '
    );
    
    // Handle lists without attributes
    processedContent = processedContent.replace(
      /<ul>/gi,
      '<ul class="content-list content-ul">'
    );
    
    processedContent = processedContent.replace(
      /<ol>/gi,
      '<ol class="content-list content-ol">'
    );
    
    // Add CSS classes to list items
    processedContent = processedContent.replace(
      /<li\s+/gi,
      '<li class="content-list-item" '
    );
    
    // Handle list items without attributes
    processedContent = processedContent.replace(
      /<li>/gi,
      '<li class="content-list-item">'
    );
    
    return this.sanitizer.bypassSecurityTrustHtml(processedContent);
  }

  ngOnInit() {
    this.updateMenuItems();
    this.loadOnlineHandbooks();
    this.loadFaqs();
    this.loadVideoGuides();
  }

  updateMenuItems() {
    this.items = [
      { 
        label: 'Online Handbook', 
        icon: 'pi pi-book',
        command: () => this.showSection('onlineHandbook'),
        styleClass: this.activeSection === 'onlineHandbook' ? 'active-menu-item' : ''
      },
      { 
        label: 'FAQs', 
        icon: 'pi pi-question-circle',
        command: () => this.showSection('faqs'),
        styleClass: this.activeSection === 'faqs' ? 'active-menu-item' : ''
      },
      { 
        label: 'Video Guides', 
        icon: 'pi pi-video',
        command: () => this.showSection('videoGuides'),
        styleClass: this.activeSection === 'videoGuides' ? 'active-menu-item' : ''
      }
    ];
  }

  showSection(section: 'onlineHandbook' | 'faqs' | 'videoGuides') {
    this.showOnlineHandbook = false;
    this.showFaqs = false;
    this.showVideoGuides = false;
    this.activeSection = section;

    switch (section) {
      case 'onlineHandbook':
        this.showOnlineHandbook = true;
        this.resetPagination();
        break;
      case 'faqs':
        this.showFaqs = true;
        this.resetFaqPagination();
        break;
      case 'videoGuides':
        this.showVideoGuides = true;
        this.resetVideoGuidePagination();
        break;
    }

    this.updateMenuItems();
  }

  // Reset pagination when switching to online handbook section
  resetPagination() {
    this.currentPage = 1;
    this.updateDisplayedHandbooks();
  }

  // Reset FAQ pagination when switching to FAQ section
  resetFaqPagination() {
    this.currentFaqPage = 1;
    this.updateDisplayedFaqs();
  }

  // Reset video guide pagination when switching to video guide section
  resetVideoGuidePagination() {
    this.currentVideoGuidePage = 1;
    this.updateDisplayedVideoGuides();
  }

  // Load online handbooks
  loadOnlineHandbooks() {
    this.loading = true;
    this.onlineHandbookService.getAllOnlineHandbooks().subscribe({
      next: (response) => {
        // Sort by createdAt in descending order (latest first)
        this.onlineHandbooks = response.data.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          } else if (a.createdAt) {
            return -1; // a comes first if b has no createdAt
          } else if (b.createdAt) {
            return 1; // b comes first if a has no createdAt
          }
          return 0; // both have no createdAt, maintain original order
        });
        this.updateDisplayedHandbooks();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading online handbooks:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load online handbooks'
        });
        this.loading = false;
      }
    });
  }

  // Update displayed handbooks based on current page
  updateDisplayedHandbooks() {
    const startIndex = 0;
    const endIndex = this.currentPage * this.itemsPerPage;
    this.displayedHandbooks = this.onlineHandbooks.slice(startIndex, endIndex);
    this.hasMoreHandbooks = endIndex < this.onlineHandbooks.length;
  }

  // Load more handbooks
  loadMoreHandbooks() {
    this.currentPage++;
    this.updateDisplayedHandbooks();
  }

  // Load FAQs
  loadFaqs() {
    this.faqLoading = true;
    this.faqService.getAllFaqs().subscribe({
      next: (response) => {
        // Sort by createdAt in descending order (latest first)
        this.faqs = response.data.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          } else if (a.createdAt) {
            return -1; // a comes first if b has no createdAt
          } else if (b.createdAt) {
            return 1; // b comes first if a has no createdAt
          }
          return 0; // both have no createdAt, maintain original order
        });
        this.updateDisplayedFaqs();
        this.faqLoading = false;
      },
      error: (error) => {
        console.error('Error loading FAQs:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load FAQs'
        });
        this.faqLoading = false;
      }
    });
  }

  // Update displayed FAQs based on current page
  updateDisplayedFaqs() {
    const startIndex = 0;
    const endIndex = this.currentFaqPage * this.faqsPerPage;
    this.displayedFaqs = this.faqs.slice(startIndex, endIndex);
    this.hasMoreFaqs = endIndex < this.faqs.length;
  }

  // Load more FAQs
  loadMoreFaqs() {
    this.currentFaqPage++;
    this.updateDisplayedFaqs();
  }

  // Load video guides
  loadVideoGuides() {
    this.videoGuideLoading = true;
    this.videoGuideService.getAllVideoGuides().subscribe({
      next: (response) => {
        // Sort by createdAt in descending order (latest first)
        this.videoGuides = response.data.sort((a, b) => {
          if (a.createdAt && b.createdAt) {
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
          } else if (a.createdAt) {
            return -1; 
          } else if (b.createdAt) {
            return 1; 
          }
          return 0;
        });
        this.updateDisplayedVideoGuides();
        this.videoGuideLoading = false;
      },
      error: (error) => {
        console.error('Error loading video guides:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load video guides'
        });
        this.videoGuideLoading = false;
      }
    });
  }

  // Update displayed video guides based on current page
  updateDisplayedVideoGuides() {
    const startIndex = 0;
    const endIndex = this.currentVideoGuidePage * this.videoGuidesPerPage;
    this.displayedVideoGuides = this.videoGuides.slice(startIndex, endIndex);
    this.hasMoreVideoGuides = endIndex < this.videoGuides.length;
  }

  // Load more video guides
  loadMoreVideoGuides() {
    this.currentVideoGuidePage++;
    this.updateDisplayedVideoGuides();
  }

  // Open drawer for creating new handbook
  openCreateHandbook() {
    this.editingHandbook = null;
    this.onlineHandBookDrawer = true;
  }

  // Open drawer for editing handbook
  openEditHandbook(handbook: IOnlineHandbook) {
    this.editingHandbook = { ...handbook };
    this.onlineHandBookDrawer = true;
  }

  // Delete handbook
  deleteHandbook(handbook: IOnlineHandbook) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${handbook.title}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        if (handbook._id) {
          this.onlineHandbookService.deleteOnlineHandbook(handbook._id).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Handbook deleted successfully'
              });
              this.loadOnlineHandbooks();
            },
            error: (error) => {
              console.error('Error deleting handbook:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete handbook'
              });
            }
          });
        }
      }
    });
  }

  onUpload(event: any) {
    console.log('File uploaded:', event);
  }

  // Drawer event handlers
  onOnlineHandbookSubmit(formData: any) {
    // Set loading state
    if (this.onlineHandbookDrawer) {
      this.onlineHandbookDrawer.setSubmitLoading(true);
    }

    if (this.editingHandbook) {
      // Update existing handbook
      if (this.editingHandbook._id) {
        this.onlineHandbookService.updateOnlineHandbook(this.editingHandbook._id, formData).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Handbook updated successfully'
            });
            this.onlineHandBookDrawer = false;
            this.loadOnlineHandbooks();
            if (this.onlineHandbookDrawer) {
              this.onlineHandbookDrawer.setSubmitLoading(false);
              this.onlineHandbookDrawer.resetForm();
            }
          },
          error: (error) => {
            console.error('Error updating handbook:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update handbook'
            });
            if (this.onlineHandbookDrawer) {
              this.onlineHandbookDrawer.setSubmitLoading(false);
            }
          }
        });
      }
    } else {
      // Create new handbook
      this.onlineHandbookService.createOnlineHandbook(formData).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Handbook created successfully'
          });
          this.onlineHandBookDrawer = false;
          this.loadOnlineHandbooks();
          if (this.onlineHandbookDrawer) {
            this.onlineHandbookDrawer.setSubmitLoading(false);
            this.onlineHandbookDrawer.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating handbook:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create handbook'
          });
          if (this.onlineHandbookDrawer) {
            this.onlineHandbookDrawer.setSubmitLoading(false);
          }
        }
      });
    }
  }

  // FAQ methods
  openCreateFaq() {
    this.editingFaq = null;
    this.faqDrawer = true;
  }

  openEditFaq(faq: IFaq) {
    this.editingFaq = { ...faq };
    this.faqDrawer = true;
  }

  deleteFaq(faq: IFaq) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${faq.question}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        if (faq._id) {
          this.faqService.deleteFaq(faq._id).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'FAQ deleted successfully'
              });
              this.loadFaqs();
            },
            error: (error) => {
              console.error('Error deleting FAQ:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete FAQ'
              });
            }
          });
        }
      }
    });
  }

  onFaqSubmit(formData: any) {
    // Set loading state
    if (this.faqDrawerComponent) {
      this.faqDrawerComponent.setSubmitLoading(true);
    }

    if (this.editingFaq) {
      // Update existing FAQ
      if (this.editingFaq._id) {
        this.faqService.updateFaq(this.editingFaq._id, formData).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'FAQ updated successfully'
            });
            this.faqDrawer = false;
            this.loadFaqs();
            if (this.faqDrawerComponent) {
              this.faqDrawerComponent.setSubmitLoading(false);
              this.faqDrawerComponent.resetForm();
            }
          },
          error: (error) => {
            console.error('Error updating FAQ:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update FAQ'
            });
            if (this.faqDrawerComponent) {
              this.faqDrawerComponent.setSubmitLoading(false);
            }
          }
        });
      }
    } else {
      // Create new FAQ
      this.faqService.createFaq(formData).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'FAQ created successfully'
          });
          this.faqDrawer = false;
          this.loadFaqs();
          if (this.faqDrawerComponent) {
            this.faqDrawerComponent.setSubmitLoading(false);
            this.faqDrawerComponent.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating FAQ:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create FAQ'
          });
          if (this.faqDrawerComponent) {
            this.faqDrawerComponent.setSubmitLoading(false);
          }
        }
      });
    }
  }

  // Video Guide methods
  openCreateVideoGuide() {
    this.editingVideoGuide = null;
    this.videoGuideDrawer = true;
  }

  openEditVideoGuide(videoGuide: IVideoGuide) {
    this.editingVideoGuide = { ...videoGuide };
    this.videoGuideDrawer = true;
  }

  deleteVideoGuide(videoGuide: IVideoGuide) {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete "${videoGuide.title}"?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        if (videoGuide._id) {
          this.videoGuideService.deleteVideoGuide(videoGuide._id).subscribe({
            next: (response) => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Video guide deleted successfully'
              });
              this.loadVideoGuides();
            },
            error: (error) => {
              console.error('Error deleting video guide:', error);
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to delete video guide'
              });
            }
          });
        }
      }
    });
  }

  onVideoGuideSubmit(formData: any) {
    // Set loading state
    if (this.videoGuideDrawerComponent) {
      this.videoGuideDrawerComponent.setSubmitLoading(true);
    }

    if (this.editingVideoGuide) {
      // Update existing video guide
      if (this.editingVideoGuide._id) {
        this.videoGuideService.updateVideoGuide(this.editingVideoGuide._id, formData).subscribe({
          next: (response) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Video guide updated successfully'
            });
            this.videoGuideDrawer = false;
            this.loadVideoGuides();
            if (this.videoGuideDrawerComponent) {
              this.videoGuideDrawerComponent.setSubmitLoading(false);
              this.videoGuideDrawerComponent.resetForm();
            }
          },
          error: (error) => {
            console.error('Error updating video guide:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update video guide'
            });
            if (this.videoGuideDrawerComponent) {
              this.videoGuideDrawerComponent.setSubmitLoading(false);
            }
          }
        });
      }
    } else {
      // Create new video guide
      this.videoGuideService.createVideoGuide(formData).subscribe({
        next: (response) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Video guide created successfully'
          });
          this.videoGuideDrawer = false;
          this.loadVideoGuides();
          if (this.videoGuideDrawerComponent) {
            this.videoGuideDrawerComponent.setSubmitLoading(false);
            this.videoGuideDrawerComponent.resetForm();
          }
        },
        error: (error) => {
          console.error('Error creating video guide:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create video guide'
          });
          if (this.videoGuideDrawerComponent) {
            this.videoGuideDrawerComponent.setSubmitLoading(false);
          }
        }
      });
    }
  }

  onFileUpload(event: any) {
    console.log('File uploaded:', event);
  }

  onHandbookImageError(event: any, handbook: IOnlineHandbook) {
    console.error('Failed to load handbook icon:', {
      handbook: handbook.title,
      iconUrl: handbook.icon,
      error: event
    });
    // Set icon to null to show the fallback icon
    handbook.icon = '';
  }

  onVideoGuideImageError(event: any, videoGuide: IVideoGuide) {
    console.error('Failed to load video guide thumbnail:', {
      videoGuide: videoGuide.title,
      thumbnailUrl: videoGuide.thumbnail,
      error: event
    });
    // Set a fallback image or placeholder
    const target = event.target as HTMLImageElement;
    target.style.display = 'none';
    // You could also set a placeholder image here
    // videoGuide.thumbnail = 'path/to/placeholder.png';
  }
}
